import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

const EMPTY = {
  title: "", excerpt: "", content: "", cover_image: "", category: "Local Guide",
  tags: [], author: "Regency Inn & Suites", status: "published", read_time: 5,
  seo_title: "", seo_description: "", keywords: [],
};

const CATEGORIES = ["Local Guide", "Family Guide", "Business Guide", "Extended Stay", "Pet Friendly Guide", "Seasonal"];

export default function PostEditor() {
  const { user, ready } = useAuth();
  const { id } = useParams();
  const nav = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (ready && !user) nav("/admin/login");
  }, [ready, user, nav]);

  useEffect(() => {
    if (id && user) {
      api.adminPosts().then((r) => {
        const p = r.data.find((x) => x.id === id);
        if (p) setForm({ ...EMPTY, ...p });
      });
    }
  }, [id, user]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.uploadImage(fd);
      set("cover_image", data.url);
      toast.success("Image uploaded");
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      ...form,
      read_time: Number(form.read_time) || 5,
      tags: Array.isArray(form.tags) ? form.tags : String(form.tags).split(",").map((s) => s.trim()).filter(Boolean),
      keywords: Array.isArray(form.keywords) ? form.keywords : String(form.keywords).split(",").map((s) => s.trim()).filter(Boolean),
    };
    try {
      if (id) await api.updatePost(id, payload);
      else await api.createPost(payload);
      toast.success("Saved");
      nav("/admin");
    } catch (err) {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const inputCls = "mt-1 w-full border border-stone/25 rounded px-4 py-2.5 bg-transparent focus:border-amber outline-none";

  if (!ready || !user) return <div className="min-h-screen bg-bone flex items-center justify-center text-stone">Loading…</div>;

  return (
    <div className="min-h-screen bg-bone">
      <header className="bg-forest text-bone">
        <div className="max-w-4xl mx-auto px-6 py-6 flex items-center gap-4">
          <Link to="/admin" className="hover:text-amber transition-colors"><ArrowLeft size={20} /></Link>
          <h1 className="font-serif text-3xl">{id ? "Edit Post" : "New Post"}</h1>
        </div>
      </header>

      <form onSubmit={save} className="max-w-4xl mx-auto px-6 py-12 space-y-6" data-testid="post-editor-form">
        <div>
          <label className="text-xs uppercase tracking-widest text-stone">Title</label>
          <input value={form.title} onChange={(e) => set("title", e.target.value)} className={inputCls} data-testid="editor-title" required />
        </div>
        <div className="grid sm:grid-cols-2 gap-6">
          <div>
            <label className="text-xs uppercase tracking-widest text-stone">Category</label>
            <select value={form.category} onChange={(e) => set("category", e.target.value)} className={inputCls} data-testid="editor-category">
              {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-stone">Status</label>
            <select value={form.status} onChange={(e) => set("status", e.target.value)} className={inputCls} data-testid="editor-status">
              <option value="published">Published</option>
              <option value="draft">Draft</option>
            </select>
          </div>
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-stone">Cover Image</label>
          <div className="flex flex-wrap items-center gap-3 mt-1">
            <input value={form.cover_image} onChange={(e) => set("cover_image", e.target.value)} className={inputCls + " flex-1 min-w-[240px]"} data-testid="editor-cover" placeholder="Paste an image URL or upload →" />
            <label className="cursor-pointer rounded-full bg-forest text-bone px-5 py-2.5 text-sm font-medium hover:bg-amber transition-colors whitespace-nowrap" data-testid="editor-upload-label">
              {uploading ? "Uploading…" : "Upload Image"}
              <input type="file" accept="image/*" onChange={handleUpload} className="hidden" data-testid="editor-upload-input" disabled={uploading} />
            </label>
          </div>
          {form.cover_image && (
            <img src={form.cover_image} alt="cover preview" className="mt-3 h-40 w-auto rounded-md object-cover border border-stone/15" data-testid="editor-cover-preview" />
          )}
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-stone">Excerpt</label>
          <textarea value={form.excerpt} onChange={(e) => set("excerpt", e.target.value)} rows={2} className={inputCls} data-testid="editor-excerpt" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-stone">Content (HTML supported)</label>
          <textarea value={form.content} onChange={(e) => set("content", e.target.value)} rows={14} className={`${inputCls} font-mono text-sm`} data-testid="editor-content" />
        </div>
        <div className="grid sm:grid-cols-2 gap-6">
          <div>
            <label className="text-xs uppercase tracking-widest text-stone">Read time (min)</label>
            <input type="number" value={form.read_time} onChange={(e) => set("read_time", e.target.value)} className={inputCls} data-testid="editor-readtime" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-stone">Tags (comma separated)</label>
            <input value={Array.isArray(form.tags) ? form.tags.join(", ") : form.tags} onChange={(e) => set("tags", e.target.value)} className={inputCls} data-testid="editor-tags" />
          </div>
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-stone">SEO Description</label>
          <textarea value={form.seo_description} onChange={(e) => set("seo_description", e.target.value)} rows={2} className={inputCls} data-testid="editor-seo" />
        </div>
        <div className="flex gap-4 pt-2">
          <button type="submit" disabled={saving} data-testid="editor-save" className="rounded-full bg-amber text-bone px-8 py-3 font-medium hover:bg-amber-deep transition-colors disabled:opacity-60">
            {saving ? "Saving…" : "Save Post"}
          </button>
          <Link to="/admin" className="rounded-full border border-forest text-forest px-8 py-3 font-medium hover:bg-forest hover:text-bone transition-colors">
            Cancel
          </Link>
        </div>
      </form>
    </div>
  );
}
